/**
 * The size you just set, drawn on the model the way CAD draws it.
 *
 * Typing a length into a side panel leaves you checking a number against
 * a shape by eye. A dimension laid on the model closes that: witness
 * lines off each end, a dimension line between them with arrowheads, and
 * the value at its middle. It fades out on its own, so it reads as
 * feedback on an edit rather than as an annotation the scene now carries.
 */

import { useEffect, useMemo, useState } from "react";
import { Html, Line } from "@react-three/drei";
import * as THREE from "three";

import { useViewerStore } from "../store";

/** How long the whole thing stays up, fade included. */
const LIFETIME_MS = 2600;
/** The tail of that spent fading out. */
const FADE_MS = 500;

/** How far off the model the dimension line sits, as a fraction of the length it measures. */
const OFFSET_RATIO = 0.42;
/** ...but never so close that it overlaps a stout module, nor so far it flies off. */
const OFFSET_MIN = 0.05;
const OFFSET_MAX = 0.16;

/** How far past the dimension line the value sits, so it never covers the part. */
const LABEL_GAP = 0.045;

const ARROW_RATIO = 0.06;
const ARROW_MAX = 0.018;
const COLOUR = "#ff8c1a";

/**
 * A direction across the dimension to push the drawing out along.
 *
 * Any perpendicular reads fine, so this picks the world axis the
 * dimension leans on least: for the upright modules that is horizontal,
 * which is where a drawing would put it anyway.
 */
function offsetDirection(axis: THREE.Vector3): THREE.Vector3 {
  const candidates = [new THREE.Vector3(1, 0, 0), new THREE.Vector3(0, 1, 0), new THREE.Vector3(0, 0, 1)];
  const least = candidates.reduce((best, v) =>
    Math.abs(v.dot(axis)) < Math.abs(best.dot(axis)) ? v : best
  );
  return new THREE.Vector3().crossVectors(axis, least).normalize();
}

export function DimensionAnnotation() {
  const hint = useViewerStore((state) => state.dimensionHint);
  const clearDimensionHint = useViewerStore((state) => state.clearDimensionHint);
  // The fade is a state flip driving a CSS transition rather than a
  // keyframe animation: the label lives inside a drei <Html> portal that
  // re-inserts its markup, and re-inserting a @keyframes rule restarts
  // any animation using it, so a keyframed fade sat at its first frame
  // forever and the label never became visible.
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (!hint) return;
    setFading(false);
    const fade = window.setTimeout(() => setFading(true), LIFETIME_MS - FADE_MS);
    const done = window.setTimeout(clearDimensionHint, LIFETIME_MS);
    return () => {
      window.clearTimeout(fade);
      window.clearTimeout(done);
    };
  }, [hint, clearDimensionHint]);

  const geometry = useMemo(() => {
    if (!hint) return null;
    const start = new THREE.Vector3(...hint.start);
    const end = new THREE.Vector3(...hint.end);
    const span = new THREE.Vector3().subVectors(end, start);
    const length = span.length();
    if (length < 1e-6) return null;

    const axis = span.clone().normalize();
    const offset = offsetDirection(axis).multiplyScalar(
      Math.min(Math.max(length * OFFSET_RATIO, OFFSET_MIN), OFFSET_MAX)
    );
    const from = start.clone().add(offset);
    const to = end.clone().add(offset);
    const outward = offsetDirection(axis).multiplyScalar(LABEL_GAP);

    // Witness lines overshoot the dimension line a little, as on a drawing.
    const overshoot = offset.clone().multiplyScalar(1.12);
    const arrow = Math.min(length * ARROW_RATIO, ARROW_MAX);
    const barb = offsetDirection(axis).multiplyScalar(arrow * 0.36);

    const head = (tip: THREE.Vector3, into: THREE.Vector3) => {
      const back = tip.clone().add(into.clone().multiplyScalar(arrow));
      return [
        [tip.toArray(), back.clone().add(barb).toArray()],
        [tip.toArray(), back.clone().sub(barb).toArray()],
      ] as [number, number, number][][];
    };

    return {
      witness: [
        [start.toArray(), start.clone().add(overshoot).toArray()],
        [end.toArray(), end.clone().add(overshoot).toArray()],
      ] as [number, number, number][][],
      dimension: [from.toArray(), to.toArray()] as [number, number, number][],
      arrows: [...head(from, axis), ...head(to, axis.clone().negate())],
      label: from.clone().add(to).multiplyScalar(0.5).add(outward),
    };
  }, [hint]);

  if (!hint || !geometry) return null;

  return (
    <group key={hint.seq}>
      {geometry.witness.map((points, i) => (
        <Line key={`w${i}`} points={points} color={COLOUR} lineWidth={1.5} transparent opacity={fading ? 0 : 0.75} />
      ))}
      <Line points={geometry.dimension} color={COLOUR} lineWidth={2} transparent opacity={fading ? 0 : 1} />
      {geometry.arrows.map((points, i) => (
        <Line key={`a${i}`} points={points} color={COLOUR} lineWidth={2} transparent opacity={fading ? 0 : 1} />
      ))}
      <Html position={geometry.label} center style={{ pointerEvents: "none" }}>
        <div
          style={{
            opacity: fading ? 0 : 1,
            transition: `opacity ${FADE_MS}ms ease-out`,
            background: "rgba(20, 20, 20, 0.82)",
            border: `1px solid ${COLOUR}`,
            borderRadius: 4,
            color: "#fff",
            font: "600 12px/1.1 ui-monospace, SFMono-Regular, Menlo, monospace",
            padding: "3px 7px",
            whiteSpace: "nowrap",
          }}
        >
          {hint.label}
        </div>
      </Html>
    </group>
  );
}
